import { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../utils/firebase';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import {doc, getDoc} from "firebase/firestore";
import Api from "../utils/Api";

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userName, setUserName] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [hasPaid, setHasPaid] = useState(false);
    const [hasVip, setHasVip] = useState(false);
    const [hasWeeklyOnly, setHasWeeklyOnly] = useState(false);
    const [hasCodeTicketAccess, setHasCodeTicketAccess] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                await setCurrentUser(user);
            }
        });

        return () => unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        checkPaymentExists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, userName])

    useEffect(() => {
        getUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser])

    const getUserData = async() => {
        if(currentUser) {
            const userDb = process.env.REACT_APP_FIREBASE_USER_DB;
            const userDocRef = doc(db, userDb, currentUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                setUserName(userData.name || '');
                setCustomerId(userData.customerID)
            } else {
                setUserName('User');
            }
        }
        setLoaded(true);
    }

    const checkPaymentExists = async () => {
        setHasPaid(false)
        setHasVip(false)
        setHasWeeklyOnly(false)
        setHasCodeTicketAccess(false)

        const data = await Api.checkSubscription(customerId)
        if(data.length > 0) {
            const actionableSubs = data.filter(sub => (sub.status === "active" || sub.status === "trialing" || sub.status === "past_due"));
            if(actionableSubs.length > 0){
                setHasSubscription(true);
            }
            let activeSubs = data.filter(sub => (sub.status === "active" || sub.status === "trialing"));

            const vipSubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_VIP_PRICE_ID);

            const vipOnlySubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_VIP_ONLY_PRICE_ID);

            const weekUpgradeSubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_WEEK_UPGRADE_PRICE_ID);

            if(vipOnlySubs.length > 0 || weekUpgradeSubs.length > 0){
                activeSubs = activeSubs
                    .filter(sub => sub.plan.id !== process.env.REACT_APP_STRIPE_VIP_ONLY_PRICE_ID)
                    .filter(sub => sub.plan.id !== process.env.REACT_APP_STRIPE_WEEK_UPGRADE_PRICE_ID)
            }

            if (activeSubs.length > 0) {
                setHasPaid(true)
            }

            const isVip = vipSubs.length > 0 || vipOnlySubs.length > 0 || weekUpgradeSubs.length > 0;
            if (isVip) {
                setHasVip(true)
            }

            // abonamentele de 3 si 6 luni au acces la fel ca VIP la sectiunea Bilete cu cod Superbet
            const sub3MonthsSubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_3_PRICE_ID);
            const sub6MonthsSubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_6_PRICE_ID);
            if (isVip || sub3MonthsSubs.length > 0 || sub6MonthsSubs.length > 0) {
                setHasCodeTicketAccess(true)
            }

            const weeklySubs = activeSubs.filter(sub => sub.plan.id === process.env.REACT_APP_STRIPE_WEEK_PRICE_ID);
            setHasWeeklyOnly(!isVip && weeklySubs.length > 0 && weeklySubs.length === activeSubs.length)
        }
    };

    const impersonateUserById = async (userId) =>  {
        const response = await fetch(`${process.env.REACT_APP_BACKEND}api/getUserById`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId,
                token: process.env.REACT_APP_BACKEND_TOKEN,
            }),
        });

        if (!response.ok) {
            console.error("Eroare la impersonare");
            return;
        }

        const data = await response.json();
        await signInWithCustomToken(auth, data.token);
    }

    const impersonateUserByEmail = async (email) =>  {
        const response = await fetch(`${process.env.REACT_APP_BACKEND}api/getUserByEmail`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email,
                token: process.env.REACT_APP_BACKEND_TOKEN,
            }),
        });

        if (!response.ok) {
            console.error("Eroare la impersonare");
            return;
        }

        const data = await response.json();
        await signInWithCustomToken(auth, data.token);
    }

    const logout = async () => {
        await auth.signOut();
        await setCurrentUser(null);
        await setCustomerId('');
        await setUserName('');
        await setHasPaid(false);
        await setHasVip(false);
        await setHasCodeTicketAccess(false);
        await setHasSubscription(false);
    }

    const value = {
        currentUser,
        setCurrentUser,
        userName,
        customerId,
        hasPaid,
        setHasPaid,
        hasVip,
        setHasVip,
        hasCodeTicketAccess,
        hasWeeklyOnly,
        hasSubscription,
        logout,
        loaded,
        impersonateUserById,
        impersonateUserByEmail,
        checkPaymentExists,
        getUserData,
    };


    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};